import { ExternalLink, Newspaper, RefreshCw } from 'lucide-react';
import { useLorcanaNews } from '@/hooks/useLorcanaNews';
import { Skeleton } from '@/components/ui/skeleton';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function NewsTab() {
  const { data: items, isLoading, isFetching, refetch, error } = useLorcanaNews();

  return (
    <div className="px-4 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Newspaper className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-display font-semibold">Lorcana News</h2>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-body text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Refresh news"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      <p className="text-xs text-muted-foreground font-body">
        Auto-refreshes hourly · new sets, tournaments, and Lorcana coverage
      </p>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm font-body text-destructive">
          Couldn't load news right now. Try again in a moment.
        </div>
      )}

      {!isLoading && items && items.length === 0 && (
        <div className="rounded-xl border border-border bg-card p-6 text-center text-sm font-body text-muted-foreground">
          No recent Lorcana news found.
        </div>
      )}

      <ul className="space-y-3">
        {items?.map((item, idx) => (
          <li key={`${item.link}-${idx}`}>
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-sm font-semibold leading-snug">
                  {item.title}
                </h3>
                <ExternalLink className="h-4 w-4 flex-shrink-0 text-muted-foreground mt-0.5" />
              </div>
              {item.summary && (
                <p className="mt-2 text-xs font-body text-muted-foreground leading-relaxed line-clamp-3">
                  {item.summary}
                </p>
              )}
              <div className="mt-3 flex items-center gap-2 text-[11px] font-body text-muted-foreground">
                <span className="font-medium text-foreground/80">{item.source}</span>
                <span>·</span>
                <span>{timeAgo(item.publishedAt)}</span>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
